/**
 * THE LIVE CONVERSATIONS, ABOVE THE PLACEMENTS.
 *
 * Extracted from `AskThread` when the feed became the second surface that
 * holds a conversation, because the one thing this state must never be is
 * per-component: the single-answer guard below is global by design, and two
 * components each holding a copy of it would let two questions truncate each
 * other — the exact defect the guard exists to refuse.
 *
 * A conversation has more than one home — the feed under the thread, a
 * candidate's card in Breakthroughs — and moving between them is an ordinary
 * act: switch tabs while an answer is streaming and the mount changes, which in
 * React is an unmount and a different instance. Held in component state, the
 * answer in flight died there. The request kept running, because nothing aborts
 * it, and its deltas and its completion landed on a component that no longer
 * existed — so the paid answer was never shown and never stored, and the
 * replacement mount hydrated only what had finished BEFORE the reader
 * navigated. Money spent, nothing kept.
 *
 * So the transcript lives in module scope, keyed by exploration and pin, and
 * every mount reads the same one through `useSyncExternalStore`. The send below
 * depends on no component at all: it writes into this map and persists from
 * here, so where the reader is standing when the answer lands stops mattering.
 * It is the composer draft's latch (D36's follow-up) one level up — the same
 * finding, about state that outlives the box it was typed in.
 *
 * The draft rides along for the same reason: half a typed question is lost to a
 * home change exactly as easily as half an answer.
 */

import { useSyncExternalStore } from 'react'
import { useStore, type ChatTarget } from '../core/store'
import { ask, stopAsking, type ChatTurn } from '../engine/chat'
import {
  readThread,
  writeThread,
  clearThread,
  restoreThread,
  conversationEpoch,
  conversationOwnerTag,
  onConversationOwnerChanged,
  onConversationsForgotten,
  type StoredAbout,
  type StoredTurn,
} from '../core/conversations'
import { onRenamed } from '../core/rename'
import { quietLine, waitLine } from '../core/failure'
import type { ModelError, TextTurn } from '../engine/transport'

/**
 * One turn as a surface holds it: the engine's shape, plus when it was asked,
 * what it was about, and whether the MODEL started it (`voice` — the narrator
 * speaking after a pass, with no question above it). The extras exist for the
 * feed, which interleaves a conversation with the session's pass record and
 * labels each turn's subject — a pinned thread's key already names its one
 * subject, so `AskThread` simply never sets them.
 */
export type LiveTurn = ChatTurn & {
  at?: number
  about?: StoredAbout
  voice?: true
  /** Passes the reply proposed, as data (`engine/acts.ts`) — rendered as
   *  priced chips that only a person's press runs. The STORED shape, loose on
   *  purpose: a restored turn's rows come off disk untyped and every chip
   *  re-validates against the app's own tables at render. */
  acts?: NonNullable<StoredTurn['acts']>
  /** While live: the transport waiting to ask again (`waitLine`), or the answer
   *  gone quiet (`quietLine`, `narrationQuietLine`). Never stored. */
  waiting?: string
}

/** The stored shape of a live turn — one mapping, because two copies of "which
 *  fields persist" is how a field persists from one sender and not the other. */
function storedOf(t: LiveTurn): StoredTurn {
  return {
    id: t.id,
    question: t.question,
    answer: t.answer,
    ...(t.at !== undefined ? { at: t.at } : {}),
    ...(t.about ? { about: t.about } : {}),
    ...(t.voice ? { voice: t.voice } : {}),
    ...(t.acts?.length ? { acts: t.acts } : {}),
  }
}

export interface Live {
  turns: LiveTurn[]
  draft: string
}

/** `<owner>/<exploration>:<pin>` — never the pin alone, or a conversation would
 *  follow its key into a different exploration, and never without the owner,
 *  or an answer still arriving for one reader would go on writing into the
 *  conversation the next reader opens under the same exploration and pin (A01,
 *  `ConversationOwner` in core/conversations.ts). */
type LiveKey = string

const live = new Map<LiveKey, Live>()
const watchers = new Map<LiveKey, Set<() => void>>()

function announce(k: LiveKey) {
  for (const fn of watchers.get(k) ?? []) fn()
}

function put(k: LiveKey, next: Live) {
  live.set(k, next)
  announce(k)
}

/** One string a graph target can join: `{kind:'graph'}` carries no id, so a
 *  reset cannot key on `target.id` across the union. The feed does not use it —
 *  its one conversation is keyed `'feed'` whatever each turn is about. */
export function threadKeyOf(target: ChatTarget): string {
  return target.kind === 'graph' ? 'graph' : `${target.kind}:${target.id}`
}

/**
 * Forgetting reaches the live copies too.
 *
 * `core/conversations.ts` blocks the write that would put a forgotten
 * conversation back on disk; this drops the copy still in memory, so the text
 * is gone from both. Subscribed once at module load rather than at each Forget
 * button, because a rule spread over four call sites is the drift this file
 * keeps arguing against.
 */
onConversationsForgotten((sessionId, reaches, whose) => {
  for (const k of [...live.keys()]) {
    const { tag, sessionId: owner } = splitLiveKey(k)
    if (sessionId !== null && owner !== sessionId) continue
    // A forget performed here names the reader whose shelf it removed, and only
    // that reader's copies go: an acknowledged deletion can land after they
    // signed out, and the next reader's conversation is not theirs to put down
    // (D331). One heard from another tab names nobody, and reaches every copy.
    if (whose !== null && tag !== whose) continue
    // `reaches` is what makes a forget heard from ANOTHER tab stop short of a
    // conversation begun after it — see `ForgetReach`. A forget performed here
    // reaches everything it names, so this is a no-op on that path.
    if (!reaches(owner)) continue
    // The stored copy is already gone — `forgetConversations` removed the key
    // before telling anyone. What is left is the live one and, if this is the
    // conversation currently arriving, a request the reader has just discarded.
    discardConversation(k, false)
    // …unless the forget came from another tab with another READER. The record
    // a tab leaves is about the exploration and names no owner, so a personal
    // tab's Forget everything is heard here by an account's conversation whose
    // shelf it never touched — and an emptied copy trusted as current is the
    // base the next write replaces that shelf with: the reader's history gone,
    // one new turn in its place. So the next write reads the shelf first.
    //
    // At the WRITE, not at the next render. The forgetting tab writes its
    // record before it removes the transcripts, so this announcement can
    // arrive while a key it is about to remove still reads; a render-time
    // re-read would put a forgotten conversation back on screen. By the time
    // somebody presses Ask, that removal has landed, and the read finds either
    // nothing — a forget that reached this shelf — or this reader's own turns.
    rereadBeforeWrite.add(k)
  }
  // The dot an answer left goes with the conversation it pointed at — the
  // current reader's only, by the rule above.
  if (whose === null || whose === conversationOwnerTag()) {
    const kept = Object.entries(marks.answeredAt).filter(
      ([id]) => sessionId !== null && id !== sessionId,
    )
    if (kept.length !== Object.keys(marks.answeredAt).length)
      publishMarks({ ...marks, answeredAt: Object.fromEntries(kept) })
  }
})

/** `<owner>/<exploration>:<pin>`, taken apart. An owner tag carries no slash
 *  (`conversationOwnerTag`) and a session id no slash or colon (`s_<uid>`), so
 *  the first of each is the seam. */
function splitLiveKey(k: LiveKey): { tag: string; sessionId: string; key: string } {
  const slash = k.indexOf('/')
  const rest = k.slice(slash + 1)
  const at = rest.indexOf(':')
  return { tag: k.slice(0, slash), sessionId: rest.slice(0, at), key: rest.slice(at + 1) }
}

/**
 * Where the one answer currently arriving is writing to.
 *
 * Mutable, and read through rather than copied out, so a rename can redirect a
 * request that is already in flight. Null whenever nothing is arriving.
 */
let inFlight: { k: LiveKey; tag: string; sessionId: string; key: string } | null = null

/**
 * Live copies put down when their reader left, to be read from the shelf again
 * on next sight rather than trusted. See the owner handler below.
 */
const rehydrate = new Set<LiveKey>()

/**
 * Live copies put down by a forget heard from another tab, whose shelf may
 * still hold this reader's turns: read again before the next write is built on
 * them. See the forget handler above, and `rebase` below.
 */
const rereadBeforeWrite = new Set<LiveKey>()

/**
 * Bring a copy put down by another tab's forget back to what the shelf holds,
 * before a write is built on it. Every path that writes a conversation calls
 * this first, after `ensureLive`; the draft is the box and is kept.
 */
function rebase(sessionId: string, key: string): void {
  const k = liveKey(sessionId, key)
  if (!rereadBeforeWrite.delete(k)) return
  const cur = live.get(k)
  live.set(k, {
    turns: readThread(sessionId, key).map((t) => ({ ...t, live: false })),
    draft: cur?.draft ?? '',
  })
  announce(k)
}

/**
 * The exploration was renamed under us — same graph, new name.
 *
 * Only the demo does this, and only when a paid pass turns the worked example
 * into the reader's own exploration. The store says so (`core/rename.ts`);
 * this does not guess, because the version that guessed moved conversations
 * onto graphs they were not about.
 *
 * Three things move, and missing any one of them is its own bug. The stored
 * transcript is already handled in `core/conversations.ts`, which subscribes
 * ahead of this one. Here it is the live copies, the guard's idea of which
 * conversation is arriving, and the in-flight request's own destination — that
 * last is why `sendOn` holds an object instead of closing over its key: left
 * pointing at the old name, an answer would go on patching an entry nothing
 * reads and then be filed under an id the demo is about to reuse.
 *
 * This runs synchronously inside the store's write, before React re-renders on
 * the new id, so the mounted panel finds its conversation already moved rather
 * than hydrating an empty one from a key that no longer holds anything.
 *
 * The CURRENT reader's copies only, because the shelf moved only theirs: the
 * rename is what that reader's pass did, and another reader's copies were put
 * down when they left and will be read again from their own shelf, where the
 * transcript still sits under the old name.
 */
onRenamed(({ from, to }) => {
  const now = conversationOwnerTag()
  for (const [k, v] of [...live]) {
    const { tag, sessionId, key } = splitLiveKey(k)
    if (tag !== now || sessionId !== from) continue
    const next = liveKey(to, key, tag)
    live.delete(k)
    live.set(next, v)
    if (rehydrate.delete(k)) rehydrate.add(next)
    if (rereadBeforeWrite.delete(k)) rereadBeforeWrite.add(next)
  }
  // A cleared conversation's put-back follows it too — its epoch was taken
  // under the old name, and the shelf carried the count across with the rest.
  for (const [k, v] of [...putAside]) {
    const { tag, sessionId, key } = splitLiveKey(k)
    if (tag !== now || sessionId !== from) continue
    putAside.delete(k)
    putAside.set(liveKey(to, key, tag), v)
  }
  if (streaming !== null) {
    const { tag, sessionId, key } = splitLiveKey(streaming.k)
    if (tag === now && sessionId === from) streaming = { k: liveKey(to, key, tag), n: streaming.n }
  }
  if (inFlight !== null && inFlight.tag === now && inFlight.sessionId === from) {
    inFlight.sessionId = to
    inFlight.k = liveKey(to, inFlight.key, inFlight.tag)
  }
  // The narrator's flights are the same fact by another door — see `speakOn`.
  for (const f of voiceFlights) if (f.tag === now && f.sessionId === from) f.sessionId = to
  announceAll()
})

/**
 * The reader changed — a sign-in, a sign-out, another account — and every
 * conversation in memory belongs to the one who left.
 *
 * The shelf below is already keyed by owner, so the next read is the new
 * reader's. What this puts down is everything that was never on the shelf:
 * the live copies, with the drafts typed into them, the answer still arriving
 * and any narration speaking. Left alone, each of those was a way for one
 * reader's words to reach the next — on the screen they were already on, or
 * patched into the conversation the next reader opens under the same graph.
 *
 * The epoch the shelf moved on the same change drops whatever those requests
 * would have written, so stopping them here is about the money and the screen,
 * not the record.
 *
 * Emptied and marked rather than deleted, `discardConversation`'s rule: a mount
 * may still be reading its old key until it renders again. Marked so that the
 * owner's own return reads their shelf again instead of an emptied copy that
 * would show them nothing where their conversation is.
 */
onConversationOwnerChanged(() => {
  const now = conversationOwnerTag()
  for (const k of [...live.keys()]) {
    // Already put down at an earlier change — a sign-in is two of them, out to
    // nobody and in to the account — and nothing can be started under a tag
    // that is not the current one, so there is nothing new in it to stop.
    if (splitLiveKey(k).tag === now || rehydrate.has(k)) continue
    discardConversation(k, false)
    rehydrate.add(k)
  }
  // The turns a Clear set aside were never on any shelf either — the Clear had
  // just taken them off it. They are the last reader's words, so they go now
  // rather than when somebody presses a toast still on screen (D339). Even a
  // sign-in back to the same reader drops them: the epoch they were taken
  // under has moved, and the write would refuse anyway.
  for (const [k, v] of [...putAside]) putAside.set(k, { n: v.n, dropped: 'reader' })
  // Belt and braces for a request whose live copy is somehow gone: what is
  // arriving for the reader who left is stopped by its tag, not found by its
  // entry.
  if (streaming !== null && splitLiveKey(streaming.k).tag !== now) {
    stopAsking()
    streaming = null
  }
  for (const f of [...voiceFlights]) if (f.tag !== now) f.stop?.()
  // …and the marks the last reader's conversations left on the sidebar, which
  // were never on any shelf either (D334).
  publishMarks({ arrivingIn: [], answeredAt: {} })
  announceAll()
})

/**
 * Put a conversation down — THE ONE WAY, and every route that discards one
 * goes through here.
 *
 * There are three: the Clear on a live conversation, the Clear on one whose
 * node or candidate has since gone, and an exploration being forgotten out
 * from under it. They were written separately and drifted immediately: one
 * stopped the answer and deleted the transcript, one released the pin and
 * left the transcript on disk under a button that said Clear, and one dropped
 * the live copy while the request went on billing with no Stop control and the
 * single-answer guard still raised against every other conversation.
 *
 * The rule they all needed is one sentence — stop what is arriving, drop the
 * live copy, and erase the stored one where the reader asked for that — so it
 * is written once.
 *
 * An erase sets the finished turns aside for the Clear's "Put it back" (D339),
 * and every other discard drops what was set aside: the paths that do not
 * erase are a forget and a reader leaving, and neither leaves anything to come
 * back to. Putting one BACK is a second function, `restoreConversation`, and
 * not a mode of this one.
 */
export function discardConversation(k: LiveKey, erase: boolean) {
  // Stop FIRST. Every path here is the reader putting the conversation down,
  // and an answer nobody can see any more is still spending and still holding
  // the guard shut on every other conversation.
  if (streamingOn(k)) {
    stopAsking()
    streaming = null
    announceAll()
  }
  // …and a narration speaking into it, which is its own request on its own
  // controller: left running it went on billing into a conversation nobody had.
  stopVoiceOn(k)
  // Erased only from the shelf of the reader the copy belongs to. The shelf
  // takes the current reader's, so a Clear landing on a copy left over from the
  // last one — a mount that has not rendered since the change — would otherwise
  // clear the same pin on the NEW reader's shelf. Nothing is set aside from such
  // a copy either: it holds nothing (the owner change emptied it), and its
  // put-back would be refused as the last reader's.
  const { tag, sessionId, key } = splitLiveKey(k)
  const mine = tag === conversationOwnerTag()
  if (erase && mine) {
    // What the reader saw finished, in the shape the shelf keeps — `storedOf`
    // over the same filter every write uses, so a put-back stores exactly what
    // an answer's own write would have. The count is taken NOW: a forget after
    // this press makes the put-back stale, as it does an answer in flight.
    const turns = (live.get(k)?.turns ?? [])
      .filter((t) => !t.live && !t.error && t.answer)
      .map(storedOf)
    const n = ++putAsideSeq
    if (turns.length > 0) putAside.set(k, { n, turns, epoch: conversationEpoch(sessionId) })
    else putAside.delete(k)
    clearThread(sessionId, key)
  } else if (!erase) {
    // A forget, or the owner handler on its way to saying the reader changed.
    // The words go; the reason stays, so the toast can say it.
    const held = putAside.get(k)
    if (held !== undefined) putAside.set(k, { n: held.n, dropped: 'forgotten' })
  }
  // Emptied rather than deleted: a mount reads its entry during render, and a
  // missing one would have to be re-hydrated from a store this may have just
  // erased. An empty entry holds no text either way.
  live.set(k, { turns: [], draft: '' })
  announce(k)
}

/**
 * THE TURNS A CLEAR TOOK, HELD FOR ITS "PUT IT BACK" (D339).
 *
 * Every Ask answer is a model call the reader paid for, and Clear sat one tap
 * from "Go to it" with no confirm and no way back — the one removal of the
 * reader's own work in the app that did neither. Everything else that takes
 * something off confirms in place or puts up an undo toast; this is the toast.
 *
 * Held HERE rather than in the toast's closure, because the toast lives in the
 * store and outlives the reader: a conversation belongs to its reader (D330),
 * and a closure over the turns would carry one reader's words across a sign-in
 * for as long as the toast stood. Here the owner handler can put them down, and
 * a forget replaces them with the reason they went — never the words.
 *
 * `n` names the Clear, so a toast left from an earlier one cannot put back a
 * later one's turns: two Clears on one conversation are two offers, and the
 * older one refuses — `undoOffer`'s identity rule, for the same reason.
 */
type PutAside =
  { n: number; turns: StoredTurn[]; epoch: number } | { n: number; dropped: 'forgotten' | 'reader' }

const putAside = new Map<LiveKey, PutAside>()
let putAsideSeq = 0

/**
 * Where a Clear's turns sit now. Found by the Clear's number, not only by the key
 * the toast took: a paid pass on the worked example renames the exploration under
 * the offer (`onRenamed` moves the entry), and the toast's key would then name
 * nothing. `n` is unique across every Clear, so it names one entry wherever it is.
 */
function putAsideAt(at: LiveKey, n: number): LiveKey {
  if (putAside.get(at)?.n === n) return at
  return [...putAside].find(([, v]) => v.n === n)?.[0] ?? at
}

/** Why a put-back refused, or that it did not. */
export type Restored = 'restored' | 'reader' | 'forgotten' | 'since' | 'taken'

/**
 * Put a cleared conversation back, if nothing since has made that wrong.
 *
 * Four things do, and each refuses rather than merging: the reader changed (the
 * turns are not this reader's — D330), the exploration was forgotten (here, in
 * another tab, or by the epoch moving — D331), the conversation has gone on
 * since (a turn arrived, or a later Clear), or another tab has written it. Two
 * conversations spliced together is a transcript nobody had.
 *
 * It lights no mark (D334): nothing landed, the reader is putting back what they
 * were already reading.
 */
export function restoreConversation(at: LiveKey, n: number): Restored {
  const k = putAsideAt(at, n)
  const { tag, sessionId, key } = splitLiveKey(k)
  if (tag !== conversationOwnerTag()) return 'reader'
  const held = putAside.get(k)
  if (held === undefined || held.n !== n) return 'since'
  if ('dropped' in held) return held.dropped
  const cur = live.get(k)
  if (cur !== undefined && cur.turns.length > 0) return 'since'
  const wrote = restoreThread(sessionId, key, held.turns, Date.now(), held.epoch)
  if (wrote === 'stale') {
    putAside.set(k, { n, dropped: 'forgotten' })
    return 'forgotten'
  }
  if (wrote === 'taken') return 'taken'
  putAside.delete(k)
  // Read from the shelf it was just written to: the copy and the record agree,
  // and anything put down to be re-read is current again. The box is kept.
  rereadBeforeWrite.delete(k)
  live.set(k, {
    turns: held.turns.map((t) => ({ ...t, live: false })),
    draft: cur?.draft ?? '',
  })
  announce(k)
  return 'restored'
}

/** What a refused put-back says. One sentence each, never the words it held. */
const PUT_BACK_REFUSED: Record<Exclude<Restored, 'restored'>, string> = {
  reader: 'Not put back — the reader changed, and that conversation was the last one’s.',
  forgotten: 'Not put back — the exploration was forgotten since.',
  since: 'Not put back — the conversation has gone on since the Clear.',
  taken: 'Not put back — another tab has written this conversation since.',
}

/**
 * The reader's Clear, from a press — the discard, then the offer.
 *
 * The forget path never comes through here: it puts a conversation down with
 * nothing to come back to, so it offers nothing. A Clear with nothing finished
 * in it offers nothing either — there is nothing to put back, and a toast with
 * no way back is only noise.
 *
 * A refused put-back CONSUMES its toast and says why in another. The store's
 * convention is that `false` keeps an offer standing to be taken once whatever
 * blocked it clears (`undoOffer`'s busy refusal) — but nothing here clears: a
 * reader who changed, an exploration forgotten, a conversation gone on, a tab
 * that wrote it, are each permanent for this Clear. Kept standing, the button
 * would be dead and still asking to be pressed.
 *
 * `after` runs once the turns are back — the candidate card's thread re-pins
 * itself with it, because its Clear also closed it. It is handed the
 * exploration id the turns went back under, which a rename since the Clear has
 * made a different one from the id the thread was drawn with.
 */
export function clearConversation(k: LiveKey, after?: (sessionId: string) => void): void {
  discardConversation(k, true)
  const held = putAside.get(k)
  if (held === undefined || 'dropped' in held) return
  const n = held.n
  useStore.getState().toast('Conversation cleared.', 'info', {
    label: 'Put it back',
    run: () => {
      const { sessionId } = splitLiveKey(putAsideAt(k, n))
      const r = restoreConversation(k, n)
      if (r === 'restored') after?.(sessionId)
      else useStore.getState().toast(PUT_BACK_REFUSED[r], 'warn')
    },
  })
}

/**
 * The one REQUEST currently streaming, anywhere.
 *
 * `engine/chat.ts` holds a single controller and aborts it at the top of every
 * `ask`, so a second question does not run beside the first — it KILLS it. With
 * the transcript surviving remounts, that abort stopped being invisible and
 * became a lie: the first answer returns its partial text with no error, and
 * would be shown, and stored, as though the model had finished saying it.
 *
 * So the guard is global rather than per-pin. Asking is refused while another
 * conversation is still arriving, in the composer's own words — "one thought at
 * a time" — instead of quietly spending on a second question and truncating the
 * first. A per-conversation controller would be the other answer, and it lives
 * in the engine, which this module does not touch.
 *
 * It names the REQUEST, not the conversation, and that distinction is the whole
 * point of the serial number. Clear aborts a request and releases the guard;
 * asking the same target again starts a second request under the same key. When
 * the first one's abort finally resolves it still recognised the key as its own,
 * released a guard the second request was holding, and let a third through —
 * whose `ask` truncated the second and stored its partial text as a finished
 * answer. A key says which conversation; only the serial says which attempt.
 */
let requestSeq = 0
let streaming: { k: LiveKey; n: number } | null = null

/**
 * The guard changed, so every conversation has to look again.
 *
 * A fresh `Live` object per watcher, and that is the whole point rather than a
 * flourish: each surface's `useSyncExternalStore` snapshot IS its `Live`
 * entry, while "another answer is arriving" is derived from `streaming`, which
 * is deliberately not in it. React compares the snapshot with `Object.is` and
 * skips the render when it is unchanged, so notifying alone is inert here —
 * releasing the guard touches nothing a blocked conversation can see.
 *
 * That was a real defect, not a theoretical one. The panel refusing input went
 * on saying another answer was still arriving after it had landed, holding a
 * question handed over from the composer behind a disabled box whose only
 * enabled control erases it. It unstuck itself only when something unrelated
 * happened to re-render the thread, which made it look intermittent. Worse, the
 * browser suite that asserts the release PASSED throughout, on exactly those
 * incidental re-renders — a green test for a mechanism that never ran.
 *
 * The contents are untouched; only the identity changes, which is what React is
 * comparing.
 */
function announceAll() {
  for (const k of [...watchers.keys()]) {
    const cur = live.get(k)
    if (cur) live.set(k, { ...cur })
    announce(k)
  }
  // Every change to what is arriving passes through here, the rename's
  // redirect included — so the sidebar's ring is kept in the same breath.
  syncArriving()
}

/**
 * WHERE AN ANSWER IS ARRIVING, AND WHERE ONE LANDED UNSEEN — the sidebar's
 * half of a conversation (D334). An answer keeps arriving after the reader
 * opens another exploration, which is this module's whole point, and until
 * this nothing said where it had gone: ask in A, open B, and A's answer
 * finished with no sign of it anywhere B's reader could see.
 *
 * So the rows say it: a ring on the exploration an answer — or a narration —
 * is still arriving in, and a filled dot on one it landed in while the reader
 * was in another. Only AWAY from the open exploration: an answer arriving in
 * the thread the reader just typed in is being watched, and a dot after every
 * Ask would be noise. Opening the exploration puts the dot out through the
 * ordinary stamp (`markOpened`), which is newer than the landing.
 *
 * MEMORY ONLY, and that is the cost accepted rather than an oversight: a
 * conversation belongs to its reader (D330) and every slot the sidebar could
 * read from is the device's, so a stored landing would tell the next reader on
 * this device that somebody had a conversation there. A reload forgets it.
 * The current reader's conversations only, for the same reason — a reader
 * change puts the marks down with everything else in memory.
 *
 * The snapshot changes identity only when something in it changed, or
 * `useSyncExternalStore` would render without end.
 */
export interface ConversationMarks {
  /** Explorations an answer or a narration is arriving in now, for the current
   *  reader. Usually one at most; a narration beside an answer makes two. */
  arrivingIn: readonly string[]
  /** When an answer landed in an exploration that was not the open one, by id. */
  answeredAt: Readonly<Record<string, number>>
}

let marks: ConversationMarks = { arrivingIn: [], answeredAt: {} }
const markWatchers = new Set<() => void>()

function publishMarks(next: ConversationMarks): void {
  marks = next
  for (const fn of [...markWatchers]) fn()
}

function subscribeMarks(fn: () => void): () => void {
  markWatchers.add(fn)
  return () => {
    markWatchers.delete(fn)
  }
}

/** The marks, for a component — re-rendered only when one changes. */
export function useConversationMarks(): ConversationMarks {
  return useSyncExternalStore(
    subscribeMarks,
    () => marks,
    () => marks,
  )
}

/** The same snapshot, read once — for a test, and for anything outside React. */
export const conversationMarks = (): ConversationMarks => marks

/** Recompute where something is arriving, off `streaming` and the narrator's
 *  flights, and publish only on a change. */
function syncArriving(): void {
  const now = conversationOwnerTag()
  const ids = new Set<string>()
  if (streaming !== null) {
    const { tag, sessionId } = splitLiveKey(streaming.k)
    if (tag === now) ids.add(sessionId)
  }
  for (const f of voiceFlights) if (f.tag === now) ids.add(f.sessionId)
  const next = [...ids].sort()
  const cur = marks.arrivingIn
  if (next.length === cur.length && next.every((id, i) => id === cur[i])) return
  publishMarks({ ...marks, arrivingIn: next })
}

/** An answer or a narration finished in `sessionId` for the reader `tag`: a
 *  dot, unless that exploration is the open one or the reader has changed. */
function landedAway(sessionId: string, tag: string): void {
  if (tag !== conversationOwnerTag()) return
  if (useStore.getState().session.id === sessionId) return
  publishMarks({ ...marks, answeredAt: { ...marks.answeredAt, [sessionId]: Date.now() } })
}

/** Whether anything is arriving for this conversation. */
export function streamingOn(k: LiveKey): boolean {
  return streaming !== null && streaming.k === k
}

/** Whether an answer is arriving for some OTHER conversation — the reading the
 *  single-answer guard refuses a send on. Read at render time, not from the
 *  snapshot; `announceAll` is what makes a change here reach every mount. */
export function streamingElsewhere(k: LiveKey): boolean {
  return streaming !== null && streaming.k !== k
}

/** Whether ANY answer is arriving. The composer asks before it clears its box
 *  and hands a question over — a handoff the feed cannot take should stay in
 *  the box, not fall between the two surfaces (D37). */
export function anyStreaming(): boolean {
  return streaming !== null
}

function subscribeTo(k: LiveKey) {
  return (fn: () => void) => {
    const set = watchers.get(k) ?? new Set()
    set.add(fn)
    watchers.set(k, set)
    return () => {
      set.delete(fn)
      if (set.size === 0) watchers.delete(k)
    }
  }
}

/** The conversation on this exploration and pin, for the current reader unless
 *  a caller holding one from earlier names theirs. */
export function liveKey(
  sessionId: string,
  key: string,
  tag: string = conversationOwnerTag(),
): LiveKey {
  return `${tag}/${sessionId}:${key}`
}

/**
 * Hydrate on first sight of this pin, in the render that first asks for it —
 * an effect would paint a conversation's empty state for one frame before the
 * stored transcript replaced it. Idempotent, so StrictMode's second pass and
 * every later render are no-ops.
 *
 * Always the CURRENT reader's, because the shelf it reads from is. A copy put
 * down when its reader left is read again here, once, when that reader is back.
 */
export function ensureLive(sessionId: string, key: string): void {
  const k = liveKey(sessionId, key)
  if (!live.has(k) || rehydrate.delete(k)) {
    // Read from the shelf, so there is nothing left to re-read before a write.
    rereadBeforeWrite.delete(k)
    live.set(k, {
      turns: readThread(sessionId, key).map((t) => ({ ...t, live: false })),
      draft: '',
    })
  }
}

/** One conversation, live, for a component — hydrated, subscribed, snapshot. */
export function useLiveConversation(sessionId: string, key: string): Live {
  const k = liveKey(sessionId, key)
  ensureLive(sessionId, key)
  return useSyncExternalStore(
    subscribeTo(k),
    () => live.get(k)!,
    () => live.get(k)!,
  )
}

export function setLiveDraft(sessionId: string, key: string, v: string): void {
  const k = liveKey(sessionId, key)
  ensureLive(sessionId, key)
  put(k, { ...live.get(k)!, draft: v })
}

/**
 * How a question ended, for the surface that sent it: a failure's kind, or
 * `keyless` when there was no key to send it with.
 */
export type AskEnd = ModelError['kind'] | 'keyless'

/**
 * Whether a question that ended this way goes back to the box it left.
 *
 * Every failure but the reader's own Stop. A key refusal always did — rejected
 * or missing, beside the key field `askForKey` has just opened; a rate
 * limit, an overload or a dropped connection left the reader told to "wait a
 * minute, then try again" with the question gone from the box — the words to
 * try again with typed a second time. The failed turn still says what happened;
 * the question is simply there to press Enter on.
 */
export const handsBack = (end: AskEnd | undefined): boolean =>
  end !== undefined && end !== 'aborted'

/**
 * One question, asked and kept without a component's help.
 *
 * What matters is that the whole path from here to `writeThread` touches no
 * React state, so an unmount mid-answer costs nothing.
 *
 * `about` is the feed's per-turn subject — stored with the turn, because the
 * feed's one conversation ranges over many things and a turn that cannot say
 * what it was about is not a record. A pinned thread passes nothing: its key
 * already names its one subject.
 */
/**
 * Ask one question on one conversation. Resolves, once the answer has finished
 * or failed, to how it ended when this request was still the one that mattered
 * — so the surface that sent it can hand a question refused for its KEY back to
 * the box it came from (the Settings sheet is already open on that key,
 * `askForKey`). Nothing else reads it; every other outcome is in the turn.
 */
export function sendOn(
  target: ChatTarget,
  sessionId: string,
  key: string,
  question: string,
  about?: StoredAbout,
): Promise<AskEnd | undefined> {
  // WHERE this request is writing, held in one mutable object rather than closed
  // over as constants — a rename has to be able to redirect it while the answer
  // is still arriving, and a closure cannot be edited from outside. See the
  // `onRenamed` handler above. At most one of these exists at a time, which is
  // the single-answer guard's doing. The OWNER is taken now and never moves:
  // this answer is for the reader who asked, whoever is reading when it lands.
  const tag = conversationOwnerTag()
  const here = { k: liveKey(sessionId, key, tag), tag, sessionId, key }
  inFlight = here
  // Hydrated first, because the history and the write below both start from
  // the live copy: a copy put down when this reader last left and not yet seen
  // again would send no history, and its completion would write the shelf back
  // with this one turn on it. Rebased for the same reason, after a forget
  // heard from another tab that may not have reached this reader's shelf.
  ensureLive(sessionId, key)
  rebase(sessionId, key)
  const before = live.get(here.k)?.turns ?? []
  const now = Date.now()
  const id = `t${now}`
  // Taken NOW and offered back at the end: a forget landing in between makes
  // this write stale and it is dropped. See `epochs` in core/conversations.ts.
  // A rename carries the count across with the transcript, so offering back a
  // count taken under the old name is correct under the new one.
  const epoch = conversationEpoch(sessionId)
  const n = ++requestSeq
  streaming = { k: here.k, n }
  put(here.k, {
    turns: [
      ...before,
      { id, question, answer: '', live: true, at: now, ...(about ? { about } : {}) },
    ],
    draft: '',
  })
  announceAll()

  // The history the model sees: prior turns only, and only the ones that
  // actually produced an answer. Narrator turns stay out — the shape below is
  // question/answer pairs, and a turn with no question would hand the API an
  // empty user message; what the narration described is in the brief anyway,
  // because it was derived from the same session record.
  const history: TextTurn[] = before
    .filter((t) => !t.live && !t.error && t.answer && !t.voice)
    .flatMap((t) => [
      { role: 'user' as const, text: t.question },
      { role: 'assistant' as const, text: t.answer },
    ])

  const patch = (fn: (t: LiveTurn) => LiveTurn) => {
    const cur = live.get(here.k)
    if (!cur) return
    put(here.k, { ...cur, turns: cur.turns.map((t) => (t.id === id ? fn(t) : t)) })
  }

  // Proposals ride every feed subject; the pinned candidate threads stay the
  // plain channel — a bridge names two nodes, and a single-node pass proposed
  // against "the pair" would be the app guessing which end to spend on.
  return ask(
    target,
    question,
    history,
    (delta) => {
      patch((t) => ({ ...t, answer: t.answer + delta }))
    },
    {
      proposeActs: target.kind !== 'bridge',
      // Said on the turn while the transport waits to ask again, and gone as it
      // asks — an empty answer with a caret through a minute's rate limit read
      // as a model thinking.
      onWait: (w) => patch((t) => ({ ...t, waiting: w.ms > 0 ? waitLine(w) : undefined })),
      // …and on the same line once the answer has gone quiet, a lone pass's
      // words: this turn's Stop keeps what arrived, as the pass's does.
      onQuiet: (ms) => patch((t) => ({ ...t, waiting: ms === null ? undefined : quietLine(ms) })),
    },
  ).then(({ text, error, errorKind, keyless, acts }) => {
    const end: AskEnd | undefined = keyless ? 'keyless' : errorKind
    // Whether this request is still the one that matters. It is not if the
    // reader put its conversation down, or if a later question took the
    // engine's single controller out from under it. Either way it must not
    // release a guard it no longer holds, and must not write.
    const mine = streaming !== null && streaming.n === n
    if (mine) {
      streaming = null
      announceAll()
    }
    // Still patched when superseded, and this is the point of the whole change:
    // an abort resolves with the text that HAD arrived and no error, so a turn
    // left saying that verbatim is a truncated answer wearing a finished one's
    // face. It says so instead, and is not stored. A superseded or failed turn
    // also offers no chips — a priced button under an answer that says it was
    // cut off would be a press about half a thought.
    patch((t) => ({
      ...t,
      answer: text,
      live: false,
      waiting: undefined,
      error: mine ? error : (error ?? 'Stopped before it finished.'),
      ...(mine && !error && acts?.length ? { acts } : {}),
    }))
    if (inFlight === here) inFlight = null
    if (!mine) return undefined
    // News on return either way — an answer, or what went wrong — so a reader
    // who left before it landed is shown where it did (D334). A superseded or
    // discarded request returned above, and leaves no mark.
    landedAway(here.sessionId, here.tag)
    // Store what finished. A turn that errored never arrived; one the reader
    // STOPPED is kept, because this app's own rule for a stop is that "a
    // partial answer is still an answer, and it was billed" (`stopAsking`).
    const cur = live.get(here.k)
    if (!cur) return end
    writeThread(
      here.sessionId,
      here.key,
      cur.turns.filter((t) => !t.live && !t.error && t.answer).map(storedOf),
      Date.now(),
      epoch,
    )
    return end
  })
}

/**
 * A message the MODEL starts — the narrator's channel into the same
 * conversation the reader's questions live in.
 *
 * Deliberately OUTSIDE the single-answer guard: that guard exists because
 * `engine/chat.ts` holds one controller and a second `ask` kills the first.
 * The narrator drives its own request on its own controller (`ui/narrator.ts`),
 * so nothing here can truncate an answer — and a live voice turn still holds
 * the feed's send closed the ordinary way, because a send refuses while ANY
 * turn in the conversation is live.
 *
 * Returns handles rather than taking a callback, because the caller owns the
 * request's lifecycle. `here` is mutable and registered for the same reason
 * `inFlight` is: the demo's paid remint renames the exploration while text is
 * arriving, and a closure over the old name would go on patching an entry
 * nothing reads and then file the transcript under an id the demo is about to
 * reuse. A Clear while the voice is arriving stops it and empties the live
 * entry — the patches below then find nothing, and `finish` persists the
 * emptied conversation, which is exactly what Clear meant.
 *
 * `stop` is the speaker's own way to end it, handed in because this module
 * cannot reach the narrator's controller (the narrator imports this one). A
 * flight carries its turn's id so a Stop under one narration ends that one.
 */
const voiceFlights = new Set<{
  /** The reader it speaks to, fixed at its start — see `sendOn`'s `tag`. */
  tag: string
  sessionId: string
  key: string
  id: string
  stop?: () => void
}>()

/**
 * Stop one narration — the Stop under its own words. Its request is aborted and
 * nothing else: not the pass it follows (D51), not an answer arriving elsewhere.
 * The speaker then finishes the turn in its own sentence (`narrationStopped`).
 */
export function stopNarration(turnId: string): void {
  for (const f of [...voiceFlights]) if (f.id === turnId) f.stop?.()
}

function stopVoiceOn(k: LiveKey): void {
  for (const f of [...voiceFlights]) if (liveKey(f.sessionId, f.key, f.tag) === k) f.stop?.()
}

/**
 * Stop whatever is arriving in this conversation: the answer to a question asked
 * here, and any narration speaking into it. The phone's box Stop, which shows
 * while ANY turn here is live — a narration included — and called `stopAsking`
 * alone, so under a narration it stopped nothing, or an answer arriving in some
 * other conversation.
 */
export function stopLive(k: LiveKey): void {
  if (streamingOn(k)) stopAsking()
  stopVoiceOn(k)
}

export interface VoiceHandle {
  /** The turn's id — what the Stop under its words names (`stopNarration`). */
  readonly id: string
  append(delta: string): void
  /** A line under the words, not one of them — the narration gone quiet — or
   *  null to take it away. Never stored, like an Ask turn's `waiting`. */
  note(line: string | null): void
  /** `cutByLeaving`: the line stopped short BECAUSE the reader left its
   *  exploration — an authored line on a timer, which only runs where it is
   *  watched. Nothing landed while they were away, so it leaves no dot. */
  finish(
    text: string,
    error?: string,
    acts?: NonNullable<StoredTurn['acts']>,
    opts?: { cutByLeaving?: boolean },
  ): void
}

/** When the newest voice turn was minted — see `speakOn`'s id. */
let lastVoiceAt = 0

export function speakOn(
  sessionId: string,
  key: string,
  about?: StoredAbout,
  stop?: () => void,
): VoiceHandle {
  // A millisecond past the last voice turn at least, so no two share an id: a
  // Stop names its turn by it, and so does React's key.
  const now = Math.max(Date.now(), lastVoiceAt + 1)
  lastVoiceAt = now
  const id = `v${now}`
  const here = { tag: conversationOwnerTag(), sessionId, key, id, ...(stop ? { stop } : {}) }
  voiceFlights.add(here)
  syncArriving()
  ensureLive(sessionId, key)
  rebase(sessionId, key)
  // Taken NOW and offered back at the end, `sendOn`'s own rule: a forget
  // landing mid-narration makes the write stale, and it is dropped.
  const epoch = conversationEpoch(sessionId)
  const k0 = liveKey(sessionId, key, here.tag)
  const cur0 = live.get(k0)!
  put(k0, {
    ...cur0,
    turns: [
      ...cur0.turns,
      {
        id,
        question: '',
        answer: '',
        live: true,
        at: now,
        voice: true,
        ...(about ? { about } : {}),
      },
    ],
  })

  const patch = (fn: (t: LiveTurn) => LiveTurn) => {
    const kk = liveKey(here.sessionId, here.key, here.tag)
    const cur = live.get(kk)
    if (!cur) return
    put(kk, { ...cur, turns: cur.turns.map((t) => (t.id === id ? fn(t) : t)) })
  }

  return {
    id,
    append(delta) {
      patch((t) => ({ ...t, answer: t.answer + delta }))
    },
    note(line) {
      patch((t) => ({ ...t, waiting: line ?? undefined }))
    },
    finish(text, error, acts, opts) {
      // No chips on a failed narration — `sendOn`'s own rule, same reason: a
      // priced button under half a thought is a press about nothing.
      patch((t) => ({
        ...t,
        answer: text,
        live: false,
        waiting: undefined,
        ...(error ? { error } : {}),
        ...(!error && acts?.length ? { acts } : {}),
      }))
      voiceFlights.delete(here)
      syncArriving()
      const kk = liveKey(here.sessionId, here.key, here.tag)
      const cur = live.get(kk)
      if (!cur) return
      // A dot where it landed, if that is not where the reader is (D334) — but
      // not for a narration whose conversation was put down under it: a Clear
      // or a forget empties the copy, and there is nothing there to read. Nor
      // for a line the reader's leaving cut short: it ended because they went,
      // not while they were gone, and a dot on the exploration they just left
      // would call a half-line unread.
      if (!opts?.cutByLeaving && cur.turns.some((t) => t.id === id)) {
        landedAway(here.sessionId, here.tag)
      }
      writeThread(
        here.sessionId,
        here.key,
        cur.turns.filter((t) => !t.live && !t.error && t.answer).map(storedOf),
        Date.now(),
        epoch,
      )
    },
  }
}

/**
 * Replace the words of one AUTHORED voice turn, in the live copy and on the
 * shelf — the worked example's line, whose earlier wording is no longer true
 * (D295). Revised in place rather than spoken again beneath itself, because two
 * authored lines stacked in one conversation, one of them wrong, is worse than
 * one line that is right.
 *
 * The narrator is the one caller, and only for a turn whose stored text it has
 * matched against its OWN earlier wordings (`demoLineDue`), so the model's
 * words never reach here: nothing a model said is ever edited. A turn that is
 * not a voice turn, or is not there, is left alone.
 */
export function reviseAuthored(
  sessionId: string,
  key: string,
  id: string,
  answer: string,
  acts?: NonNullable<StoredTurn['acts']>,
): void {
  ensureLive(sessionId, key)
  rebase(sessionId, key)
  const k = liveKey(sessionId, key)
  const cur = live.get(k)!
  if (!cur.turns.some((t) => t.id === id && t.voice)) return
  // Taken and offered back in one breath — there is no request in between for
  // a forget to land inside, but the write goes through the same door as
  // every other so a forgotten exploration still refuses it.
  const epoch = conversationEpoch(sessionId)
  const turns = cur.turns.map((t) =>
    t.id === id ? { ...t, answer, ...(acts?.length ? { acts } : {}) } : t,
  )
  put(k, { ...cur, turns })
  writeThread(
    sessionId,
    key,
    turns.filter((t) => !t.live && !t.error && t.answer).map(storedOf),
    Date.now(),
    epoch,
  )
}

/** Openers, because a blank box in front of a claim is a harder question than it
 *  looks. Each one is a question this channel can actually answer without a search. */
export const OPENERS = [
  'Explain this in plainer terms.',
  'What would have to be true for this to hold?',
  'Where is this weakest?',
  'What is the strongest objection to it?',
]

/**
 * The one opener that stays: "Explain simply", on every selected node.
 *
 * The clearest text in the usability study came from Ask, and the four openers
 * above were the only invitation to it — shown until the first turn, which after
 * the first pass is always the voice's narration, and then gone for good. So the
 * feed keeps ONE chip for as long as a node is selected and the box is empty.
 * It is an Ask like any other: a chat call on the reader's press, about the
 * selected node, with no search and no lock. The label is what the chip says;
 * the question is what the conversation records under "you".
 */
export const EXPLAIN_SIMPLY = {
  label: 'Explain simply',
  question: 'Explain this simply, in plain words.',
} as const

/**
 * Whether the composer's box holds words — the desktop's Ask box, read by the
 * feed so its chip stands down while somebody is typing a question of their
 * own. Module scope and a subscription, for the reason the draft itself is
 * latched in module scope in `Composer.tsx`: the box changes homes, and the feed
 * is not its parent. Written by the composer on every draft change.
 */
let composerWords = false
const composerListeners = new Set<() => void>()
export function noteComposerDraft(draft: string): void {
  const has = draft.trim().length > 0
  if (has === composerWords) return
  composerWords = has
  for (const fn of composerListeners) fn()
}
/** The same fact, read once — for a question handed back after a refused key,
 *  which must not overwrite words typed since. */
export const composerHasWords = (): boolean => composerWords
export function useComposerEmpty(): boolean {
  return !useSyncExternalStore(
    (fn) => {
      composerListeners.add(fn)
      return () => composerListeners.delete(fn)
    },
    () => composerWords,
    () => composerWords,
  )
}
